import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../config";
import "../../assets/css/storytelling.css";
import { PRONUNCIATION_PASS_SCORE, speakText } from "../../utils/audioHelpers";
import PronunciationPractice from "../common/PronunciationPractice";

const LANG_TAG = { 0: "de-DE", 1: "es-MX", 2: "fr-FR", 3: "ru-RU", 4: "pt-BR" };
function getLangTag(langId) {
    return LANG_TAG[parseInt(langId, 10)] ?? "de-DE";
}

function pronunciationWordClassName(word) {
    if (word.error_type === "Omission") {
        return "pronunciation-word pronunciation-word--omission";
    }
    if (word.accuracy_score >= 80) {
        return "pronunciation-word pronunciation-word--good";
    }
    if (word.accuracy_score >= 50) {
        return "pronunciation-word pronunciation-word--ok";
    }
    return "pronunciation-word pronunciation-word--poor";
}

function CheckmarkIcon({ size = 12 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function CrossIcon({ size = 12 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

function ArrowRightIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
        </svg>
    );
}

function StoryQuiz({ story, enablePronunciation, langId, onComplete }) {
    // Filter questions to only comprehension questions (no display_after_segment set)
    const quizQuestions = story.questions.filter(q => q.display_after_segment === undefined && (enablePronunciation || q.type !== "pronounce"));

    const [currentIdx, setCurrentIdx] = useState(0);
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [status, setStatus] = useState(null); // 'checking', 'correct', 'incorrect'
    const [correctIdx, setCorrectIdx] = useState(null);
    const [score, setScore] = useState(0);
    const [startQuiz, setStartQuiz] = useState(false);

    const question = quizQuestions[currentIdx];

    const [shuffledOptions, setShuffledOptions] = useState([]);

    useEffect(() => {
        if (question && question.options) {
            const mapped = question.options.map((opt, idx) => ({ originalIndex: idx, optionText: opt }));
            for (let i = mapped.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [mapped[i], mapped[j]] = [mapped[j], mapped[i]];
            }
            setShuffledOptions(mapped);
        } else {
            setShuffledOptions([]);
        }
    }, [question]);

    const handleOptionSelect = (shuffledIdx, originalIdx) => {
        if (status !== null) return; // already selected
        setSelectedIdx(shuffledIdx);
        setStatus("checking");

        const questionVal = quizQuestions[currentIdx];

        fetch(`${API_BASE_URL}/api/story/check-answer`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                story_id: story.id,
                question_id: questionVal.id,
                selected_index: originalIdx
            })
        })
            .then(res => res.json())
            .then(data => {
                if (data.correct) {
                    setStatus("correct");
                    setScore(prev => prev + 1);
                } else {
                    setStatus("incorrect");
                    setCorrectIdx(data.correct_index);
                }
            })
            .catch(err => {
                console.error(err);
                setStatus(null);
            });
    };

    const [pronunciationAttempt, setPronunciationAttempt] = useState(0);
    const [pronunciationResult, setPronunciationResult] = useState(null);

    // Reset pronunciation state when moving to next question
    const handleNext = () => {
        setSelectedIdx(null);
        setStatus(null);
        setCorrectIdx(null);
        setPronunciationAttempt(0);
        setPronunciationResult(null);

        if (currentIdx < quizQuestions.length - 1) {
            setCurrentIdx(prev => prev + 1);
        } else {
            onComplete(score, quizQuestions.length);
        }
    };

    const handlePronunciationResult = (result) => {
        const isCorrect = result.overall_score >= PRONUNCIATION_PASS_SCORE;
        setPronunciationResult(result);
        setStatus(isCorrect ? "correct" : "incorrect");
        // If they already got it wrong and reset it so that it's correct, don't mark it as a right answer:
        if (isCorrect && pronunciationAttempt === 0) {
            setScore(prev => prev + 1);
        }
    };

    const handleRedo = () => {
        // If they already got it correct, we don't double count score, but we can reset the state
        if (status === "correct") {
            setScore(prev => Math.max(0, prev - 1));
        }
        setStatus(null);
        setPronunciationResult(null);
        setPronunciationAttempt(prev => prev + 1);
    };

    if (!startQuiz) {
        return (
            <div className="quiz-intro-card">
                <h2 className="quiz-intro-card__title">Time for a Quiz!</h2>
                <button className="story-card__btn story-card__btn--primary" style={{ width: "100%", maxWidth: "200px" }} onClick={() => setStartQuiz(true)}>
                    Start Quiz
                </button>
            </div>
        );
    }

    if (quizQuestions.length === 0) {
        return (
            <div style={{ textAlign: "center", padding: "clamp(16px, 4vw, 40px)" }}>
                <p>No comprehension questions available for this story.</p>
                <button className="story-card__btn story-card__btn--primary" onClick={() => onComplete(0, 0)}>
                    Finish
                </button>
            </div>
        );
    }

    return (
        <div className="quiz-container">
            <span className="question-box__badge">
                Comprehension - Question {currentIdx + 1} of {quizQuestions.length}
            </span>
            <p className="question-box__prompt">{question.prompt}</p>

            {question.type === "pronounce" ? (
                <div style={{ marginTop: 16 }}>
                    {status === null ? (
                        <PronunciationPractice
                            key={`${question.id}-${pronunciationAttempt}`}
                            text={question.text_to_pronounce}
                            langCode={getLangTag(langId)}
                            onResult={handlePronunciationResult}
                        />
                    ) : (
                        <div className="test-pronunciation-feedback" style={{ marginTop: 16 }}>
                            <div className={`test-feedback ${status === "correct" ? "test-feedback--correct" : "test-feedback--wrong"}`}>
                                {status === "correct" ? "Excellent!" : "Keep practicing!"}
                            </div>
                            <div className="pronunciation-overall-label">Overall pronunciation score</div>
                            <div className="pronunciation-overall-score">
                                {Math.round(pronunciationResult?.overall_score || 0)}
                            </div>

                            <div className="test-pronunciation-word-list" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', margin: '16px 0' }}>
                                {pronunciationResult?.words?.map((word, index) => (
                                    <div key={`${word.word}-${index}`} className="test-pronunciation-word-item">
                                        <span
                                            className={pronunciationWordClassName(word)}
                                            onClick={() => speakText(word.word, getLangTag(langId))}
                                            title="Click to hear pronunciation"
                                        >
                                            {word.word}
                                        </span>
                                        <span className="test-pronunciation-word-score">
                                            {word.error_type === "Omission"
                                                ? "Missed"
                                                : `${Math.round(word.accuracy_score)}%`}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="test-feedback-actions" style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
                                <button type="button" className="test-redo-btn" onClick={handleRedo}>
                                    Try again
                                </button>
                                {currentIdx < quizQuestions.length - 1 && (
                                    <button type="button" className="test-submit-btn" onClick={handleNext}>
                                        Next Question
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="options-list">
                    {shuffledOptions.map((item, idx) => {
                        let className = "option-btn";
                        if (selectedIdx === idx) {
                            if (status === "correct") className += " option-btn--correct";
                            else if (status === "incorrect") className += " option-btn--incorrect";
                            else className += " option-btn--selected";
                        } else if (status === "incorrect" && correctIdx === item.originalIndex) {
                            className += " option-btn--correct";
                        }

                        return (
                            <button
                                key={idx}
                                className={className}
                                onClick={() => handleOptionSelect(idx, item.originalIndex)}
                                disabled={status !== null}
                            >
                                <span>{item.optionText}</span>
                                {selectedIdx === idx && status === "correct" && (
                                    <span className="option-indicator option-indicator--correct">
                                        <CheckmarkIcon size={12} />
                                    </span>
                                )}
                                {selectedIdx === idx && status === "incorrect" && (
                                    <span className="option-indicator option-indicator--incorrect">
                                        <CrossIcon size={12} />
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {status !== null && status !== "checking" && (
                <div className="question-feedback-row">
                    <button className="story-nav-btn story-nav-btn--next" onClick={handleNext}>
                        <span>{currentIdx === quizQuestions.length - 1 ? "Finish Quiz" : "Next Question"}</span>
                        <ArrowRightIcon size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}

export default StoryQuiz;
